/**
 * 表格绘制：圈选覆层 + 选区右上角行列确认工具栏（表格插入会话 UI）
 */
import { NumberStepper } from '@heroui-pro/react/number-stepper';
import { Button, Label } from '@heroui/react';
import type { TableDrawPending, TableDrawRect } from '@/components/canvas/elements/table/controllers/useTableDraw';
import { SelectionChromeFrame } from '@/components/canvas/selection/SelectionChromeFrame';

export interface TableDrawSessionLayerProps {
  /** 工作区缩放；工具条用 1/zoom 抵消 stage scale，保持屏幕 100% 尺寸 */
  zoom: number;
  drawRect?: TableDrawRect | null;
  pending?: TableDrawPending | null;
  onRowsChange: (rows: number) => void;
  onColsChange: (cols: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function MarqueeRectOverlay({ rect, zoom }: { rect: TableDrawRect; zoom: number }) {
  return (
    <div
      className="pointer-events-none absolute z-[1002] bg-accent/20"
      style={{
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
      }}
      aria-hidden
    >
      <SelectionChromeFrame left={0} top={0} width={rect.width} height={rect.height} zoom={zoom} />
    </div>
  );
}

export function TableDrawSessionLayer({
  zoom,
  drawRect,
  pending,
  onRowsChange,
  onColsChange,
  onConfirm,
  onCancel,
}: TableDrawSessionLayerProps) {
  const showRect = drawRect ?? pending?.rect;
  if (!showRect) return null;

  const safeZoom = zoom > 0 ? zoom : 1;
  const rows = pending?.rows ?? 5;
  const cols = pending?.cols ?? 4;

  return (
    <>
      <MarqueeRectOverlay rect={showRect} zoom={zoom} />
      {pending ? (
        <div
          data-table-draw-toolbar
          className="absolute z-[1003]"
          style={{
            left: showRect.x + showRect.width,
            top: showRect.y,
            transform: 'translate(-100%, calc(-100% - 8px))',
            pointerEvents: 'auto',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            className="flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1.5 shadow-md"
            style={{
              transform: `scale(${1 / safeZoom})`,
              transformOrigin: 'bottom right',
            }}
          >
            <div className="flex items-center gap-1.5">
              <NumberStepper
                aria-label="行数"
                size="sm"
                value={rows}
                minValue={1}
                maxValue={50}
                step={1}
                onChange={(v) => {
                  if (v == null || Number.isNaN(v)) return;
                  onRowsChange(v);
                }}
              >
                <NumberStepper.Group>
                  <NumberStepper.DecrementButton />
                  <NumberStepper.Value />
                  <NumberStepper.IncrementButton />
                </NumberStepper.Group>
              </NumberStepper>
              <Label className="text-sm text-muted">行</Label>
            </div>
            <div className="flex items-center gap-1.5">
              <NumberStepper
                aria-label="列数"
                size="sm"
                value={cols}
                minValue={1}
                maxValue={50}
                step={1}
                onChange={(v) => {
                  if (v == null || Number.isNaN(v)) return;
                  onColsChange(v);
                }}
              >
                <NumberStepper.Group>
                  <NumberStepper.DecrementButton />
                  <NumberStepper.Value />
                  <NumberStepper.IncrementButton />
                </NumberStepper.Group>
              </NumberStepper>
              <Label className="text-sm text-muted">列</Label>
            </div>
            <Button type="button" variant="primary" size="sm" onPress={onConfirm}>
              确定
            </Button>
            <Button type="button" variant="ghost" size="sm" onPress={onCancel}>
              取消
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
